// Detailed pilotage drawer for a single schedule entry.
// Opens when admin clicks a creneau in PresenceScheduleTab.
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const RACI_KEYS = [
  { key: "r", label: { fr: "Responsable (R)", en: "Responsible" }, color: "var(--cinnabar)" },
  { key: "a", label: { fr: "Approbateur (A)", en: "Accountable" }, color: "var(--gold)" },
  { key: "c", label: { fr: "Consulté (C)", en: "Consulted" }, color: "var(--lapis)" },
  { key: "i", label: { fr: "Informé (I)", en: "Informed" }, color: "var(--moss)" },
];

const MILESTONE_STATUS = {
  pending: { label: { fr: "À faire", en: "Pending" }, color: "#9a8d7a" },
  in_progress: { label: { fr: "En cours", en: "In progress" }, color: "#c98a3a" },
  done: { label: { fr: "Terminé", en: "Done" }, color: "#4f6b3a" },
  blocked: { label: { fr: "Bloqué", en: "Blocked" }, color: "#991b1b" },
};

export default function PilotageDrawer({ scheduleId, locale = "fr", onClose }) {
  const isFr = locale === "fr";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickingFor, setPickingFor] = useState(null); // raci key being edited
  const [memberSearch, setMemberSearch] = useState("");
  const [allMembers, setAllMembers] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/presence/schedule/${scheduleId}/pilotage`);
    setData(r.data);
  }, [scheduleId]);
  useEffect(() => { load(); }, [load]);

  // Load full member list on demand for picker
  const loadMembers = useCallback(async () => {
    if (allMembers.length) return;
    try {
      const r = await api.get("/admin/members");
      setAllMembers(r.data || []);
    } catch {
      // Fallback to HR overview if /admin/members not exposed
      const r2 = await api.get("/admin/hr/overview");
      setAllMembers(r2.data.members || []);
    }
  }, [allMembers.length]);

  const saveRaci = async (newRaci) => {
    setBusy(true);
    try {
      await api.patch(`/admin/presence/schedule/${scheduleId}`, { raci: newRaci });
      await load();
    } finally { setBusy(false); }
  };
  const saveNotes = async (notes) => {
    await api.patch(`/admin/presence/schedule/${scheduleId}`, { pilotage_notes: notes });
  };
  const saveMilestones = async (milestones) => {
    await api.patch(`/admin/presence/schedule/${scheduleId}`, { milestones });
    await load();
  };

  const addRaciMember = async (key, memberId) => {
    const current = data.raci || { r: [], a: [], c: [], i: [] };
    const set = new Set(current[key] || []);
    set.add(memberId);
    const newRaci = { ...current, [key]: [...set] };
    await saveRaci(newRaci);
    setPickingFor(null); setMemberSearch("");
  };
  const removeRaciMember = async (key, memberId) => {
    const current = data.raci || { r: [], a: [], c: [], i: [] };
    const newRaci = { ...current, [key]: (current[key] || []).filter((m) => m !== memberId) };
    await saveRaci(newRaci);
  };

  const memberMap = useMemo(() => {
    const m = {};
    (data?.members || []).forEach((mb) => { m[mb.id] = mb; });
    allMembers.forEach((mb) => { if (!m[mb.id]) m[mb.id] = mb; });
    return m;
  }, [data, allMembers]);

  const filteredPickerMembers = useMemo(() => {
    const q = memberSearch.toLowerCase();
    return allMembers
      .filter((m) => !q || (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [allMembers, memberSearch]);

  if (!data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(20,16,12,0.7)" }}>
        <div className="admin-card max-w-md text-center">{isFr ? "Chargement…" : "Loading…"}</div>
      </div>
    );
  }

  const e = data.entry;
  const title = e.title?.[locale] || e.title?.fr || e.title?.en;
  const slotMinutes = e.start_time ? parseHM(e.start_time) : 0;
  const endMinutes = e.end_time ? parseHM(e.end_time) : (slotMinutes + 90);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" data-testid="pilotage-drawer"
         style={{ backgroundColor: "rgba(20,16,12,0.6)" }}>
      <div className="bg-white h-full w-full max-w-4xl overflow-y-auto" style={{ borderLeft: "1px solid var(--border)" }}>
        {/* Header */}
        <div className="p-6 sticky top-0 z-10" style={{ backgroundColor: "var(--parchment)", borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="font-caps text-[0.6rem]" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
                {isFr ? "Pilotage du créneau" : "Slot pilotage"}
              </p>
              <h2 className="font-display text-2xl mt-1">{title}</h2>
              <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                📅 {e.day} · ⏱ {e.start_time}{e.end_time ? `–${e.end_time}` : ""} ·
                📍 {e.location?.[locale] || e.location?.fr || "—"}
              </p>
            </div>
            <button className="btn btn-outline" onClick={onClose} data-testid="pilotage-close">✕</button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Timeline */}
          <Section title={isFr ? "Timeline" : "Timeline"} testId="pilotage-section-timeline"
                    rightAction={
                      <button className="btn btn-outline btn-sm" data-testid="pilotage-generate-tasks"
                              onClick={async () => {
                                if (!window.confirm(isFr
                                  ? "Générer des tâches pour chaque jalon non-terminé (assignées au premier R) ?"
                                  : "Generate tasks for each non-done milestone?")) return;
                                try {
                                  const r = await api.post(`/admin/presence/schedule/${scheduleId}/generate-tasks`);
                                  alert(isFr
                                    ? `${r.data.created} tâche(s) créée(s), ${r.data.skipped} déjà existante(s).`
                                    : `${r.data.created} task(s) created, ${r.data.skipped} skipped.`);
                                  load();
                                } catch (e) {
                                  alert((isFr ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
                                }
                              }}>
                        ⚙ {isFr ? "Générer tâches" : "Generate tasks"}
                      </button>
                    }>
            <Timeline milestones={data.timeline} slotMinutes={slotMinutes} endMinutes={endMinutes}
                      onChange={saveMilestones} isFr={isFr} />
          </Section>

          {/* RACI */}
          <Section title={isFr ? "Matrice RACI" : "RACI Matrix"} testId="pilotage-section-raci">
            <div className="grid sm:grid-cols-2 gap-3">
              {RACI_KEYS.map(({ key, label, color }) => (
                <div key={key} className="admin-card" data-testid={`raci-${key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-caps text-[0.65rem]" style={{ color, letterSpacing: "0.2em" }}>{label[isFr ? "fr" : "en"]}</span>
                    <button className="btn btn-outline btn-sm"
                            data-testid={`raci-add-${key}`}
                            onClick={() => { loadMembers(); setPickingFor(key); }}>
                      + {isFr ? "Ajouter" : "Add"}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(data.raci?.[key] || []).length === 0 ? (
                      <span className="text-xs italic" style={{ color: "var(--muted)" }}>
                        {isFr ? "Aucune affectation" : "No assignment"}
                      </span>
                    ) : (
                      (data.raci?.[key] || []).map((mid) => {
                        const m = memberMap[mid];
                        return (
                          <span key={mid} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-sm"
                                style={{ backgroundColor: color + "22", color }}
                                data-testid={`raci-chip-${key}-${mid}`}>
                            {m?.name || mid}
                            <button onClick={() => removeRaciMember(key, mid)} disabled={busy}
                                    style={{ color, fontWeight: "bold" }}>×</button>
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--muted)" }}>
              {isFr ? "R = exécute · A = valide / décide · C = consulté · I = informé"
                    : "R = does it · A = decides · C = consulted · I = informed"}
            </p>
          </Section>

          {/* Notes */}
          <Section title={isFr ? "Notes de pilotage" : "Pilotage notes"} testId="pilotage-section-notes">
            <textarea className="input-classic w-full" rows={4}
                      defaultValue={data.pilotage_notes || ""}
                      onBlur={(ev) => saveNotes(ev.target.value)}
                      placeholder={isFr ? "Consignes, points d'attention, contacts d'urgence…" : "Instructions, escalations…"}
                      data-testid="pilotage-notes" />
          </Section>

          {/* Linked items */}
          <Section title={isFr ? "Éléments liés" : "Linked items"} testId="pilotage-section-related">
            <div className="grid sm:grid-cols-2 gap-3">
              <RelatedList title={isFr ? "Tâches" : "Tasks"} items={data.related.tasks}
                            renderTitle={(t) => t.title || (t.label?.fr || t.label?.en)} />
              <RelatedList title={isFr ? "Risques" : "Risks"} items={data.related.risks}
                            renderTitle={(r) => r.title || (r.label?.fr || r.label?.en)} />
              <RelatedList title={isFr ? "Communication" : "Communication"} items={data.related.comm_items}
                            renderTitle={(c) => c.title} />
              <RelatedList title={isFr ? "Restauration (même date)" : "Catering (same date)"}
                            items={data.related.catering}
                            renderTitle={(c) => `${c.meal} · ${c.provider || ""}`} />
            </div>
            {data.partners?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-caps text-[0.65rem] mb-2" style={{ letterSpacing: "0.2em", color: "var(--gold)" }}>
                  {isFr ? "Partenaires" : "Partners"}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {data.partners.map((p) => (
                    <span key={p.id} className="px-2 py-1 text-xs rounded-sm" style={{ backgroundColor: "var(--surface-2)" }}>
                      {p.name?.[locale] || p.name?.fr || p.name?.en || p.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>

      {/* Member picker overlay */}
      {pickingFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: "rgba(20,16,12,0.75)" }}>
          <div className="admin-card max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-lg">
                {isFr ? "Affecter à" : "Assign to"} {RACI_KEYS.find((r) => r.key === pickingFor)?.label[isFr ? "fr" : "en"]}
              </h4>
              <button className="btn btn-outline btn-sm" onClick={() => { setPickingFor(null); setMemberSearch(""); }}>✕</button>
            </div>
            <input className="input-classic w-full mb-3"
                   placeholder={isFr ? "Rechercher…" : "Search…"}
                   value={memberSearch} onChange={(ev) => setMemberSearch(ev.target.value)}
                   data-testid="raci-picker-search" />
            <div className="space-y-1">
              {filteredPickerMembers.map((m) => (
                <button key={m.id}
                        className="w-full text-left p-2 hover:bg-gray-50 flex items-center justify-between"
                        onClick={() => addRaciMember(pickingFor, m.id)}
                        data-testid={`raci-picker-${m.id}`}
                        style={{ border: "1px solid var(--border)" }}>
                  <span>{m.name}</span>
                  <span className="text-xs" style={{ color: "var(--muted)" }}>{m.role}</span>
                </button>
              ))}
              {filteredPickerMembers.length === 0 && (
                <p className="text-sm italic" style={{ color: "var(--muted)" }}>{isFr ? "Aucun membre" : "No member"}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, testId, children, rightAction }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center justify-between mb-3"
           style={{ borderBottom: "1px solid var(--border)", paddingBottom: 6 }}>
        <h3 className="font-display text-lg">{title}</h3>
        {rightAction || null}
      </div>
      {children}
    </section>
  );
}

function Timeline({ milestones, slotMinutes, endMinutes, onChange, isFr }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const updateField = (idx, field, val) => {
    const next = milestones.map((m, i) => i === idx ? { ...m, [field]: val } : m);
    onChange(next);
  };
  const remove = (idx) => {
    if (!window.confirm(isFr ? "Supprimer ce jalon ?" : "Delete this milestone?")) return;
    onChange(milestones.filter((_, i) => i !== idx));
  };
  const add = () => {
    const newId = `m-${Date.now()}`;
    onChange([...milestones, {
      id: newId,
      label: isFr ? "Nouveau jalon" : "New milestone",
      offset_minutes: 0,
      status: "pending",
    }]);
  };
  return (
    <div className="space-y-2">
      {milestones.map((m, idx) => {
        const st = MILESTONE_STATUS[m.status || "pending"];
        const absMin = slotMinutes + (m.offset_minutes || 0);
        const hhmm = formatHM(absMin);
        const isEditing = editingIdx === idx;
        return (
          <div key={m.id || idx} className="flex items-center gap-2 p-2 flex-wrap"
               style={{ border: "1px solid var(--border)", borderLeft: `4px solid ${st.color}` }}
               data-testid={`pilotage-milestone-${m.id || idx}`}>
            <span className="font-mono text-sm" style={{ minWidth: 60, color: "var(--cinnabar)" }}>{hhmm}</span>
            {isEditing ? (
              <>
                <input className="input-classic text-sm flex-1 min-w-[120px]" value={m.label}
                       onChange={(e) => updateField(idx, "label", e.target.value)} />
                <input type="number" className="input-classic text-sm" style={{ width: 80 }}
                       value={m.offset_minutes || 0}
                       onChange={(e) => updateField(idx, "offset_minutes", parseInt(e.target.value, 10) || 0)}
                       title={isFr ? "Décalage en minutes (négatif = avant)" : "Offset (minutes, negative = before)"} />
                <button className="btn btn-outline btn-sm" onClick={() => setEditingIdx(null)}
                        data-testid={`pilotage-milestone-done-${idx}`}>✓</button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm">{m.label}</span>
                <select className="input-classic text-xs" value={m.status || "pending"}
                        onChange={(e) => updateField(idx, "status", e.target.value)}
                        data-testid={`pilotage-milestone-status-${idx}`}>
                  {Object.entries(MILESTONE_STATUS).map(([k, l]) => (
                    <option key={k} value={k}>{l.label[isFr ? "fr" : "en"]}</option>
                  ))}
                </select>
                <button className="btn btn-outline btn-sm" onClick={() => setEditingIdx(idx)}
                        data-testid={`pilotage-milestone-edit-${idx}`}>✎</button>
                <button className="btn btn-outline btn-sm" onClick={() => remove(idx)}
                        data-testid={`pilotage-milestone-delete-${idx}`}>✕</button>
              </>
            )}
          </div>
        );
      })}
      <button className="btn btn-outline btn-sm w-full" onClick={add}
              data-testid="pilotage-milestone-add">
        + {isFr ? "Ajouter un jalon" : "Add milestone"}
      </button>
      <p className="text-xs italic mt-2" style={{ color: "var(--muted)" }}>
        {isFr ? `Créneau : ${formatHM(slotMinutes)} → ${formatHM(endMinutes)}`
              : `Slot: ${formatHM(slotMinutes)} → ${formatHM(endMinutes)}`}
      </p>
    </div>
  );
}

function RelatedList({ title, items, renderTitle }) {
  return (
    <div className="admin-card">
      <h4 className="font-caps text-[0.65rem] mb-2" style={{ letterSpacing: "0.2em", color: "var(--gold)" }}>
        {title} ({items?.length || 0})
      </h4>
      {(!items || items.length === 0) ? (
        <p className="text-xs italic" style={{ color: "var(--muted)" }}>—</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 5).map((it, i) => (
            <li key={it.id || i} className="text-sm truncate">{renderTitle(it)}</li>
          ))}
          {items.length > 5 && <li className="text-xs" style={{ color: "var(--muted)" }}>+ {items.length - 5}…</li>}
        </ul>
      )}
    </div>
  );
}

function parseHM(s) {
  const [h, m] = (s || "0:0").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}
function formatHM(min) {
  const safe = Math.max(0, min || 0);
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
